import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { createPageUrl } from "../../utils";
import { 
  Plus, TrendingUp, TrendingDown, Package, FileText, Sparkles, Settings,
  Wallet, Calculator, FileCheck, User, Truck, Wheat, MapPin, Droplet, Warehouse, Landmark
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { agrogestao } from "@/api/agrogestaoClient";

export default function QuickActions() {
  const availableActions = useMemo(() => ([
    { id: "nova_receita", label: "Nova Receita", icon: TrendingUp, page: "Financeiro?tipo=receita", color: "bg-green-500 hover:bg-green-600" },
    { id: "nova_despesa", label: "Nova Despesa", icon: TrendingDown, page: "Financeiro?tipo=despesa", color: "bg-red-500 hover:bg-red-600" },
    { id: "registrar_insumo", label: "Registrar Insumo", icon: Package, page: "Estoque", color: "bg-blue-500 hover:bg-blue-600" },
    { id: "relatorios", label: "Gerar Relatório", icon: FileText, page: "Relatorios", color: "bg-purple-500 hover:bg-purple-600" },
    { id: "simulador", label: "Simular Cenário", icon: Sparkles, page: "Simulador", color: "bg-[#D4A373] hover:bg-[#BC8A5F]" },
    { id: "novo_lancamento", label: "Novo Lançamento", icon: Wallet, page: "Financeiro", color: "bg-emerald-500 hover:bg-emerald-600" },
    { id: "conta_receber", label: "Conta a Receber", icon: TrendingUp, page: "ContasReceber", color: "bg-teal-500 hover:bg-teal-600" },
    { id: "conta_pagar", label: "Conta a Pagar", icon: TrendingDown, page: "ContasPagar", color: "bg-rose-500 hover:bg-rose-600" },
    { id: "novo_ativo", label: "Novo Ativo", icon: Truck, page: "Ativos", color: "bg-sky-500 hover:bg-sky-600" },
    { id: "nova_safra", label: "Nova Safra", icon: Wheat, page: "Safras", color: "bg-lime-500 hover:bg-lime-600" },
    { id: "novo_talhao", label: "Novo Talhão", icon: MapPin, page: "Talhoes", color: "bg-orange-500 hover:bg-orange-600" },
    { id: "registrar_leite", label: "Registrar Leite", icon: Droplet, page: "ProducaoLeite", color: "bg-cyan-500 hover:bg-cyan-600" },
    { id: "movimento_graos", label: "Movimento de Grãos", icon: Warehouse, page: "ArmazensGraos", color: "bg-yellow-500 hover:bg-yellow-600" },
    { id: "custos", label: "Centro de Custo", icon: Calculator, page: "Custos", color: "bg-indigo-500 hover:bg-indigo-600" },
    { id: "lcdpr", label: "Gerar LCDPR", icon: FileCheck, page: "LCDPR", color: "bg-stone-700 hover:bg-stone-800" },
    { id: "perfil_produtor", label: "Perfil do Produtor", icon: User, page: "PerfilProdutor", color: "bg-stone-500 hover:bg-stone-600" },
    { id: "financiamentos", label: "Financiamentos", icon: Landmark, page: "Financiamentos", color: "bg-fuchsia-500 hover:bg-fuchsia-600" },
  ]), []);

  const [email, setEmail] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [usageCounts, setUsageCounts] = useState({});
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const u = await agrogestao.auth.me();
        const em = u?.email || "";
        setEmail(em);
        const key = em ? `quick_actions_${em}` : 'quick_actions';
        const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
        const saved = raw ? JSON.parse(raw) : null;
        if (Array.isArray(saved) && saved.length > 0) {
          setSelectedIds(saved);
        } else {
          setSelectedIds(availableActions.map(a => a.id).slice(0, 5));
        }
        const uKey = em ? `quick_actions_usage_${em}` : 'quick_actions_usage';
        const uRaw = typeof localStorage !== 'undefined' ? localStorage.getItem(uKey) : null;
        const uSaved = uRaw ? JSON.parse(uRaw) : null;
        setUsageCounts(uSaved && typeof uSaved === 'object' ? uSaved : {});
      } catch {
        setSelectedIds(availableActions.map(a => a.id).slice(0, 5));
        setUsageCounts({});
      }
    })();
  }, []);

  const visibleActions = useMemo(() => {
    const indexMap = Object.fromEntries(availableActions.map((a, i) => [a.id, i]));
    const filtered = availableActions.filter(a => selectedIds.includes(a.id));
    return filtered.sort((a, b) => indexMap[a.id] - indexMap[b.id]);
  }, [availableActions, selectedIds]);
  const modalActions = useMemo(() => {
    return [...availableActions].sort((a, b) => (usageCounts[b.id] || 0) - (usageCounts[a.id] || 0));
  }, [availableActions, usageCounts]);
  const filteredModalActions = useMemo(() => {
    const s = searchTerm.trim().toLowerCase();
    if (!s) return modalActions;
    return modalActions.filter(a => a.label.toLowerCase().includes(s));
  }, [modalActions, searchTerm]);

  const toggleSelection = (id) => {
    setSelectedIds(prev => {
      const has = prev.includes(id);
      if (has) return prev.filter(x => x !== id);
      const next = [...prev, id];
      if (next.length > 5) return next.slice(0, 5);
      return next;
    });
  };

  const saveSelection = () => {
    try {
      const key = email ? `quick_actions_${email}` : 'quick_actions';
      localStorage.setItem(key, JSON.stringify(selectedIds));
      setDialogOpen(false);
    } catch {}
  };

  const persistUsage = (next) => {
    try {
      const key = email ? `quick_actions_usage_${email}` : 'quick_actions_usage';
      localStorage.setItem(key, JSON.stringify(next));
    } catch {}
  };

  const handleQuickActionClick = (action) => {
    setUsageCounts(prev => {
      const next = { ...prev, [action.id]: (prev[action.id] || 0) + 1 };
      persistUsage(next);
      return next;
    });
    try {
      agrogestao.access.logAudit({ acao: 'shortcut_click', table: 'quick_actions', status: 'success', details: { id: action.id, page: action.page } });
    } catch {}
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-lg text-stone-800 flex items-center gap-2">
          <Plus className="w-5 h-5 text-[#40916C]" />
          Ações Rápidas
        </h3>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2">
              <Settings className="w-4 h-4" />
              Editar
            </Button>
          </DialogTrigger>
          <DialogContent className="w-[95vw] sm:w-full sm:max-w-xl lg:max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Editar Ações Rápidas</DialogTitle>
              <DialogDescription>Selecione até 5 atalhos para mostrar na dashboard</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 pt-2">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <Input
                  placeholder="Buscar ação..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-1"
                />
                <div className="text-xs text-stone-600 sm:text-right">
                  Selecionadas: {selectedIds.length}/5
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {filteredModalActions.map((a) => {
                  const checked = selectedIds.includes(a.id);
                  return (
                    <label 
                      key={a.id} 
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${checked ? 'border-[#40916C] bg-[#40916C]/5' : 'border-stone-200 hover:bg-stone-50'}`}
                      onClick={() => toggleSelection(a.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <a.icon className="w-5 h-5 text-stone-700" />
                          <span className="text-sm font-medium text-stone-800">{a.label}</span>
                        </div>
                        <Checkbox checked={checked} onCheckedChange={() => toggleSelection(a.id)} />
                      </div>
                      <div className="mt-2 text-xs text-stone-500">{(usageCounts[a.id] || 0)} usos</div>
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-stone-200 mt-4 pt-3">
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">Cancelar</Button>
                <Button onClick={saveSelection} className="flex-1 bg-[#1B4332] hover:bg-[#2D6A4F]">Salvar</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {visibleActions.map((action, index) => (
          <motion.div
            key={action.label}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.05 }}
          >
            <Link
              to={createPageUrl(action.page)}
              className={`${action.color} text-white p-4 rounded-xl flex flex-col items-center gap-2 text-center transition-all shadow-sm hover:shadow-md`}
              onClick={() => handleQuickActionClick(action)}
            >
              <action.icon className="w-6 h-6" />
              <span className="text-sm font-medium">{action.label}</span>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
